import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '../../utils/icons';
import { authService } from '../../services/authService';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCGUn8Qpxc8UJIigEdDvOu3IgEAZIvTwPI';

export default function ObraModal({ obra, usuario, onClose, onSaved }) {
  const esEdicion = !!obra?.id;
  const [titulo, setTitulo] = useState(obra?.titulo || '');
  const [fecha, setFecha] = useState(obra?.fecha || '');
  const [contactoNombre, setContactoNombre] = useState(obra?.contactoNombre || '');
  const [contactoTelefono, setContactoTelefono] = useState(obra?.contactoTelefono || '');
  const [direccion, setDireccion] = useState(obra?.direccion || '');
  const [constructorNombre, setConstructorNombre] = useState(obra?.constructor || '');
  const [promotor, setPromotor] = useState(obra?.promotor || '');
  const [instalador, setInstalador] = useState(obra?.instalador || '');
  const [observaciones, setObservaciones] = useState(obra?.observaciones || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const direccionInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Autocompletado de dirección con Google Places, mismo patrón que en
  // Step1ClientIdentification.js — aquí solo se guarda la dirección
  // formateada tal cual (la obra no tiene campos separados de CP/localidad).
  useEffect(() => {
    function initAutocomplete() {
      if (!direccionInputRef.current || !window.google?.maps?.places) return;
      autocompleteRef.current = new window.google.maps.places.Autocomplete(direccionInputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'ES' },
        fields: ['formatted_address'],
        language: 'es', region: 'ES',
      });
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place?.formatted_address) setDireccion(place.formatted_address);
      });
    }

    if (window.google?.maps?.places) {
      initAutocomplete();
    } else if (!document.getElementById('google-maps-places-script')) {
      const script = document.createElement('script');
      script.id = 'google-maps-places-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places&language=es&region=ES`;
      script.async = true; script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      document.getElementById('google-maps-places-script').addEventListener('load', initAutocomplete);
    }

    return () => { if (autocompleteRef.current) window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current); };
  }, []);

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';

  async function guardar() {
    if (!titulo.trim()) { setError('El título es obligatorio'); return; }
    setSaving(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/manageObras', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          action: esEdicion ? 'actualizar' : 'crear',
          id: obra?.id,
          titulo: titulo.trim(), fecha: fecha || null,
          contactoNombre: contactoNombre.trim(), contactoTelefono: contactoTelefono.trim(),
          direccion: direccion.trim(),
          constructor: constructorNombre.trim(), promotor: promotor.trim(), instalador: instalador.trim(),
          observaciones: observaciones.trim(),
          creadorEmail: usuario.email, creadorNombre: usuario.name,
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-bold text-gray-900">{esEdicion ? 'Editar obra' : 'Nueva obra'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título de la obra" autoFocus className={inputCls} />

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">Fecha (opcional)</label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input value={contactoNombre} onChange={e => setContactoNombre(e.target.value)} placeholder="Nombre del contacto" className={inputCls} />
            <input value={contactoTelefono} onChange={e => setContactoTelefono(e.target.value)} placeholder="Teléfono del contacto" className={inputCls} />
          </div>
          <input ref={direccionInputRef} value={direccion} onChange={e => setDireccion(e.target.value)} placeholder="Empieza a escribir la dirección..." className={inputCls} />
          <input value={constructorNombre} onChange={e => setConstructorNombre(e.target.value)} placeholder="Constructor" className={inputCls} />
          <input value={promotor} onChange={e => setPromotor(e.target.value)} placeholder="Promotor" className={inputCls} />
          <input value={instalador} onChange={e => setInstalador(e.target.value)} placeholder="Instalador" className={inputCls} />
          <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)} placeholder="Observaciones (opcional)" rows={3} className={inputCls} />

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={guardar} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <Icons.Check className="w-4 h-4" />{saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
